/* Sanctura Salon PDP: native Shopify product form with scoped progressive enhancement. */
(() => {
  'use strict';
  const key = '__sancturaSalonPdpV1';
  if (window[key]) {
    window[key].init(document);
    return;
  }
  const instances = new WeakMap();
  const rootsIn = (scope) => [
    ...(scope.matches?.('[data-sn-salon]') ? [scope] : []),
    ...scope.querySelectorAll('[data-sn-salon]')
  ];
  // Only the approved layout uses this conservative split. The visible Shopify
  // description remains the fallback when no explicit specification section exists.
  function splitApprovedDescription(source, excludedMetadata = []) {
    const parts = { description: [], material: [], details: [], hasSections: false };
    let current = 'description';
    const meaningful = (node) => Boolean(node.textContent.trim() ||
      node.matches?.('img, picture, video, audio, iframe, table, svg, hr, br') ||
      node.querySelector?.('img, picture, video, audio, iframe, table, svg, hr, br'));
    const removePrefix = (node, count) => {
      for (const child of Array.from(node.childNodes)) {
        if (count <= 0) break;
        if (child.nodeType === Node.TEXT_NODE) {
          const removed = Math.min(count, child.textContent.length);
          child.textContent = child.textContent.slice(removed);
          count -= removed;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          count = removePrefix(child, count);
        }
      }
      return count;
    };
    const visit = (parent) => {
      Array.from(parent.childNodes).forEach((node) => {
        let element;
        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.textContent.trim()) return;
          element = document.createElement('p');
          element.textContent = node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (/^(SCRIPT|STYLE|TEMPLATE)$/.test(node.tagName) ||
              excludedMetadata.some((selector) => node.matches(selector))) return;
          if (node.matches('.sn-sheet-description')) {
            // A narrative field does not replace the rest of the product sheet.
            current = 'description';
            parts.description.push(node.cloneNode(true));
            return;
          }
          if (/^(DIV|SECTION|ARTICLE)$/.test(node.tagName)) {
            visit(node);
            if (node.matches('[data-sanctura-product-sheet]')) current = 'description';
            return;
          }
          element = node.cloneNode(true);
        } else return;
        const text = element.textContent;
        const label = text.match(/^\s*(Materials?|(?:Product\s+)?Details|Specifications)\s*(?:[:：]\s*|$)/i);
        if (label) {
          const group = /^material/i.test(label[1]) ? 'material' : 'details';
          parts.hasSections = true;
          removePrefix(element, label[0].length);
          if (meaningful(element)) {
            if (/^H[1-6]$/.test(element.tagName)) {
              const paragraph = document.createElement('p');
              paragraph.append(...Array.from(element.childNodes));
              element = paragraph;
            }
            parts[group].push(element);
            // "Material: Brass" is self-contained; the next paragraph may be care copy.
            current = 'description';
          } else current = group;
          return;
        }
        if (/^H[1-6]$/.test(element.tagName) ||
            /^\s*(?:(?:Product|Jewellery|Jewelry)\s+)?(?:Care|Maintenance|Warnings?|Cautions?|Important|Notes?|Please\s+note|Safety|Usage|How\s+to|Storage|Shipping|Delivery|Returns?)(?=\s|[:：]|$)/i.test(text)) {
          current = 'description';
        }
        if (meaningful(element)) parts[current].push(element);
      });
    };
    visit(source);
    return parts;
  }
  function initialise(root) {
    if (instances.has(root)) return;
    let data;
    try { data = JSON.parse(root.querySelector('[data-salon-data]').textContent); }
    catch (_) { return; }
    if (!Array.isArray(data.gallery) || !Array.isArray(data.variants)) return;
    const controller = new AbortController();
    const listen = (element, event, handler) => element?.addEventListener(event, handler, { signal: controller.signal });
    const find = (selector) => root.querySelector(selector);
    const findAll = (selector) => Array.from(root.querySelectorAll(selector));
    const status = find('[data-salon-status]');
    const form = find('.sn-salon-form');
    const selection = find('[data-salon-variant-select]');
    const input = selection || find('[data-salon-variant-input]');
    const quantity = find('[data-salon-quantity]');
    const buttons = findAll('[data-salon-submit]');
    const stage = find('[data-salon-stage]');
    const hero = find('[data-salon-hero-image]');
    const thumb = find('[data-salon-thumb-image]');
    const thumbnail = find('.thumbnail');
    const dialog = find('[data-salon-dialog]');
    const sticky = find('[data-salon-sticky]');
    const announce = (message) => { if (status) status.textContent = message; };
    let activeImage = 0;
    let activeVariant = data.variants.find((variant) => String(variant.id) === String(data.variantId));
    let returnFocus = null;
    let disposed = false;
    let observer = null;

    // Extract the store's existing sheet, without adding or inferring specifications.
    const description = find('[data-salon-description]');
    if (description) {
      const source = description.cloneNode(true);
      const sheet = source.querySelector('[data-sanctura-product-sheet]');
      const fillText = (targetSelector, sourceSelector) => {
        const sourceNode = sheet?.querySelector(sourceSelector);
        const target = find(targetSelector);
        if (sourceNode?.textContent.trim() && target) {
          target.textContent = sourceNode.textContent.trim();
          target.hidden = false;
        }
      };
      fillText('[data-salon-series]', '.sn-sheet-series');
      fillText('[data-salon-subtitle]', '.sn-sheet-subtitle');
      const approvedDescription = find('[data-salon-approved-description]');
      if (approvedDescription && root.dataset.signatureWuqu !== 'true') {
        try {
          const excludedMetadata = [
            ['[data-salon-series]', '.sn-sheet-series'],
            ['[data-salon-subtitle]', '.sn-sheet-subtitle']
          ].filter(([target, selector]) => {
            const value = sheet?.querySelector(selector)?.textContent.trim();
            return value && find(target)?.textContent.trim() === value;
          }).map(([, selector]) => selector);
          const parts = splitApprovedDescription(source, excludedMetadata);
          const replacements = ['material', 'details'].map((name) => {
            const nodes = parts[name];
            if (!nodes.length) {
              const missing = document.createElement('p');
              missing.textContent = 'Not specified.';
              nodes.push(missing);
            }
            return { target: find(`[data-salon-${name}]`), nodes };
          });
          if (parts.hasSections && parts.description.length) {
            approvedDescription.replaceChildren(...parts.description);
          }
          replacements.forEach(({ target, nodes }) => target?.replaceChildren(...nodes));
        } catch (_) {
          // Keep the server-rendered full description readable if enhancement fails.
        }
      } else {
      const groups = new Map([['details', []], ['material', []]]);
      const narrativeNodes = [];
      let current = null;
      const visit = (parent) => {
        Array.from(parent.childNodes).forEach((element) => {
          if (element.nodeType === Node.TEXT_NODE) {
            if (element.textContent.trim()) {
              const paragraph = document.createElement('p');
              paragraph.textContent = element.textContent.trim();
              (current ? groups.get(current) : narrativeNodes).push(paragraph);
            }
            return;
          }
          if (element.nodeType !== Node.ELEMENT_NODE || /^(SCRIPT|STYLE|TEMPLATE)$/.test(element.tagName)) return;
          if (element.matches('.sn-sheet-series, .sn-sheet-subtitle')) return;
          if (element.matches('.sn-sheet-description')) {
            narrativeNodes.push(element.cloneNode(true));
            current = null;
            return;
          }
          if (/^(DIV|SECTION|ARTICLE)$/.test(element.tagName)) { visit(element); return; }
          const plain = (element.textContent || '').replace(/\s+/g, ' ').trim();
          const match = plain.match(/^(Materials?|(?:Product\s+)?Details|Specifications)(?=\s|[:：]|$)\s*[:：]?\s*(.*)$/i);
          if (match) {
            current = /^material/i.test(match[1]) ? 'material' : 'details';
            if (match[2]) {
              const paragraph = document.createElement('p');
              paragraph.textContent = match[2];
              groups.get(current).push(paragraph);
            }
          } else if (/^H[1-6]$/.test(element.tagName)) {
            current = null;
            if (plain) narrativeNodes.push(element.cloneNode(true));
          } else if (/^(UL|OL)$/.test(element.tagName)) {
            groups.get(current || 'details').push(element.cloneNode(true));
          } else if (current && plain) {
            groups.get(current).push(element.cloneNode(true));
          } else if (plain || element.querySelector('img, video')) {
            narrativeNodes.push(element.cloneNode(true));
          }
        });
      };
      visit(sheet || source);
      groups.forEach((nodes, name) => {
        const target = find(`[data-salon-${name}]`);
        if (!target) return;
        if (nodes.length) target.replaceChildren(...nodes);
        else {
          const missing = document.createElement('p');
          missing.textContent = 'Not specified.';
          target.replaceChildren(missing);
        }
      });
      const editorialCopy = find('[data-salon-editorial-copy]');
      if (editorialCopy && root.dataset.signatureWuqu !== 'true') {
        const narrative = sheet?.querySelector('.sn-sheet-description');
        if (narrative?.textContent.trim()) editorialCopy.replaceChildren(narrative.cloneNode(true));
        else if (narrativeNodes.length) editorialCopy.replaceChildren(...narrativeNodes);
        else editorialCopy.hidden = true;
      }
      // Keep the complete Shopify description in its panel: unfamiliar structures
      // and specifications must remain readable even when they cannot be classified.
      }
    }

    const applyPicture = (image, picture, sourceKey) => {
      if (!image || !picture) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = picture[sourceKey] || picture.src;
      image.alt = picture.alt || picture.label || '';
      ['width', 'height'].forEach((dimension) => {
        if (Number(picture[dimension]) > 0) image.setAttribute(dimension, String(picture[dimension]));
        else image.removeAttribute(dimension);
      });
    };
    const setImage = (index, announceChange = true) => {
      if (!data.gallery.length) return;
      activeImage = ((index % data.gallery.length) + data.gallery.length) % data.gallery.length;
      const current = data.gallery[activeImage];
      const nextIndex = (activeImage + 1) % data.gallery.length;
      const next = data.gallery[nextIndex];
      applyPicture(hero, current, 'src');
      applyPicture(thumb, next, 'thumbSrc');
      const mode = current.mode || (activeImage === 0 ? 'detail' : 'full');
      stage?.classList.toggle('image-0', mode === 'detail');
      stage?.classList.toggle('image-1', mode === 'full');
      stage?.classList.toggle('image-product', mode === 'product');
      stage?.classList.toggle('image-bracelet-detail', mode === 'bracelet-detail');
      const label = next.viewLabel || `View product image ${nextIndex + 1}`;
      thumbnail?.setAttribute('aria-label', label);
      const thumbLabel = find('[data-salon-thumb-label]');
      if (thumbLabel) thumbLabel.textContent = label;
      const galleryStatus = find('[data-salon-gallery-status]');
      if (galleryStatus) {
        galleryStatus.textContent = `${current.label || 'Product image'} · ${activeImage + 1} / ${data.gallery.length}`;
        if (!announceChange) galleryStatus.setAttribute('aria-live', 'polite');
      }
    };
    if (hero) hero.hidden = data.gallery.length === 0;
    if (thumbnail) thumbnail.hidden = data.gallery.length < 2;
    const galleryNavigation = find('.gallery-navigation');
    if (galleryNavigation) galleryNavigation.hidden = data.gallery.length < 2;
    const galleryControls = find('.gallery-controls');
    if (galleryControls) galleryControls.hidden = data.gallery.length === 0;
    findAll('[data-salon-zoom="gallery"]').forEach((button) => { button.hidden = data.gallery.length === 0; });
    findAll('[data-salon-zoom="editorial"]').forEach((button) => { button.hidden = !data.editorial; });
    setImage(0, false);
    findAll('[data-salon-switch]').forEach((button) => listen(button, 'click', () => {
      setImage(activeImage + (button.dataset.salonSwitch === 'previous' ? -1 : 1));
    }));

    const updateQuantity = () => {
      if (!quantity) return;
      const min = Math.max(1, Number(activeVariant?.quantity) || 1);
      const step = Math.max(1, Number(activeVariant?.quantityStep) || 1);
      const maximum = Number(activeVariant?.quantityMax);
      const max = Number.isFinite(maximum) && maximum >= min ? maximum : null;
      const existing = Number(quantity.value);
      let next = Number.isFinite(existing) && existing >= min ? existing : min;
      if (max !== null) next = Math.min(next, max);
      next = min + Math.floor((next - min) / step) * step;
      quantity.min = String(min);
      quantity.step = String(step);
      if (max !== null) quantity.max = String(max);
      else quantity.removeAttribute('max');
      quantity.value = String(next);
      quantity.disabled = !activeVariant;
    };
    const setVariant = (id, updateUrl, syncMedia = true) => {
      activeVariant = data.variants.find((variant) => String(variant.id) === String(id));
      if (input) {
        input.value = activeVariant ? String(activeVariant.id) : '';
        input.disabled = !activeVariant;
      }
      updateQuantity();
      buttons.forEach((button) => {
        button.disabled = !form || !activeVariant?.available;
        button.textContent = activeVariant ? (activeVariant.buttonLabel || (activeVariant.available ? 'Add to cart' : 'Sold out')) : 'Unavailable';
      });
      if (activeVariant) {
        findAll('[data-salon-price]').forEach((price) => {
          const parsed = new DOMParser().parseFromString(activeVariant.price, 'text/html');
          price.textContent = parsed.body.textContent;
        });
        if (selection) selection.value = String(activeVariant.id);
        if (syncMedia && activeVariant.mediaId !== null && activeVariant.mediaId !== undefined) {
          const index = data.gallery.findIndex((picture) => String(picture.id) === String(activeVariant.mediaId));
          if (index >= 0) setImage(index, updateUrl);
        }
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(activeVariant.id));
          window.history.replaceState(window.history.state, '', url);
          announce(activeVariant.available ? 'Selected option is available.' : `Selected option: ${activeVariant.buttonLabel || 'Sold out'}.`);
        }
      }
    };
    const keepInitialMacro = root.classList.contains('bracelet-macro-product') &&
      data.gallery[0]?.mode === 'bracelet-detail';
    const keepInitialWhite = root.dataset.layoutVersion === 'wuqu-v14' &&
      root.dataset.galleryFirst === 'white';
    setVariant(data.variantId, false, !keepInitialMacro && !keepInitialWhite);
    listen(selection, 'change', () => setVariant(selection.value, true));
    listen(form, 'submit', (event) => {
      if (!activeVariant?.available) {
        event.preventDefault();
        announce('This option is not available for purchase.');
      }
      // Available submissions use the original Shopify form action and server validation.
    });

    const infoButtons = findAll('[data-salon-tab]');
    const panels = findAll('[data-salon-panel]');
    infoButtons.forEach((button) => listen(button, 'click', () => {
      const opening = button.getAttribute('aria-expanded') !== 'true';
      infoButtons.forEach((item) => {
        const active = item === button && opening;
        item.setAttribute('aria-expanded', String(active));
        item.classList.toggle('active', active);
      });
      panels.forEach((panel) => { panel.hidden = !(opening && panel.dataset.salonPanel === button.dataset.salonTab); });
    }));

    findAll('[data-salon-zoom]').forEach((button) => listen(button, 'click', () => {
      const picture = button.dataset.salonZoom === 'editorial' ? data.editorial : data.gallery[activeImage];
      if (!picture || !dialog || dialog.open) return;
      returnFocus = button;
      const dialogTitle = find('[data-salon-dialog-title]');
      if (dialogTitle) dialogTitle.textContent = picture.label || 'Product image';
      const enlarged = find('[data-salon-dialog-image]');
      applyPicture(enlarged, picture, 'zoomSrc');
      enlarged?.classList.toggle('editorial-photo', button.dataset.salonZoom === 'editorial');
      dialog.showModal();
    }));
    listen(find('[data-salon-close]'), 'click', () => dialog?.close());
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    listen(dialog, 'close', () => {
      if (!disposed && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    });

    const saveKey = 'sanctura-saved-products';
    const wishlist = find('[data-salon-wishlist]');
    let savedProducts = [];
    const readSaved = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(saveKey) || '[]');
        if (Array.isArray(stored)) savedProducts = stored.filter((entry) => typeof entry === 'string');
      } catch (_) { /* Browser storage may be unavailable; keep this page's state. */ }
    };
    const paintSaved = () => {
      const saved = savedProducts.includes(root.dataset.productHandle);
      wishlist?.setAttribute('aria-pressed', String(saved));
      wishlist?.setAttribute('aria-label', saved ? 'Remove from wishlist on this browser' : 'Save to wishlist on this browser');
    };
    readSaved();
    paintSaved();
    listen(wishlist, 'click', () => {
      readSaved();
      const handle = root.dataset.productHandle;
      const saved = savedProducts.includes(handle);
      savedProducts = saved ? savedProducts.filter((entry) => entry !== handle) : [...savedProducts, handle];
      let persisted = true;
      try { localStorage.setItem(saveKey, JSON.stringify(savedProducts)); } catch (_) { persisted = false; }
      paintSaved();
      announce(saved ? 'Removed from your wishlist on this browser.' : (persisted ? 'Saved to your wishlist on this browser.' : 'Saved for this page only; browser storage is unavailable.'));
    });
    listen(window, 'storage', (event) => { if (event.key === saveKey) { readSaved(); paintSaved(); } });

    const purchase = find('[data-salon-purchase]');
    if ('IntersectionObserver' in window && sticky && purchase) {
      observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      });
      observer.observe(purchase);
    }
    const dispose = () => {
      disposed = true;
      controller.abort();
      observer?.disconnect();
      if (dialog?.open) dialog.close();
      instances.delete(root);
      delete root.dataset.salonInitialised;
    };
    instances.set(root, { dispose });
    root.dataset.salonInitialised = 'true';
  }
  const init = (scope) => rootsIn(scope).forEach(initialise);
  window[key] = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => rootsIn(event.target).forEach((root) => instances.get(root)?.dispose()));
})();
